import { NotFound } from "@acme/ui/not-found";
import {
  createFileRoute,
  ErrorComponent,
  notFound,
} from "@tanstack/react-router";
import { ArticleView } from "~/components/blog/article-view";
import { PREVIEW_HEADERS } from "~/lib/cache-headers";
import { SECRET_URL } from "~/lib/secret-url";
import { seo } from "~/lib/seo";
import { $getArticlePreview } from "~/lib/server/blog";
import * as m from "~/paraglide/messages.js";
import { getLocale } from "~/paraglide/runtime.js";

/**
 * Reviewer links: /blog/preview/<token> and /lv/blog/preview/<token>.
 *
 * Never cached, never indexed, and no Referer leaves the page, because the URL
 * itself is the credential (`PREVIEW_HEADERS`, on the 404 for a bad or expired
 * token too). No views, likes or sharing: a reviewer is not a reader.
 * `SECRET_URL` keeps every third-party script off the page (see __root.tsx).
 */
export const Route = createFileRoute("/(public)/blog/preview/$token")({
  loader: async ({ params: { token } }) => {
    try {
      const article = await $getArticlePreview({
        data: { locale: getLocale(), token },
      });
      return { article };
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === "Preview not found" ||
          error.message === "Article not found")
      ) {
        throw notFound();
      }
      throw error;
    }
  },
  headers: () => PREVIEW_HEADERS,
  head: ({ loaderData }) => {
    const page = seo({
      noindex: true,
      path: "/blog",
      title: `${loaderData?.article?.title ?? m.blog_preview_title()} | ${m.blog_preview_title()}`,
    });
    return {
      links: [],
      meta: [
        ...page.meta.filter((tag) => !("name" in tag && tag.name === "robots")),
        { content: "noindex, nofollow", name: "robots" },
        { content: "no-referrer", name: "referrer" },
      ],
      scripts: [],
    };
  },
  staticData: SECRET_URL,
  component: PreviewPage,
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  notFoundComponent: () => <NotFound>{m.blog_preview_not_found()}</NotFound>,
});

function PreviewPage() {
  const { article } = Route.useLoaderData();

  return (
    <ArticleView
      article={article}
      banner={
        <p className="preview-banner" role="status">
          <strong>
            {article.isDraft ? m.blog_preview_draft() : m.blog_preview_live()}
          </strong>{" "}
          {m.blog_preview_note()}
        </p>
      }
    />
  );
}
